import React, { FC } from 'react'
import { Flex, Skeleton, Tag, Typography } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { Button, EmptyState, PushlyAi, Well } from '@pushly/aqe/lib/components'
import { AI_VIEW_LABELS, AiView, domainHasFlag } from './constants'
import { FEAT_INTEGRATIONS_VESPA_REC, FEAT_PUSHLY_AI } from '../../constants'
import { DomainDto } from '../../dtos/domain'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { getClassNames } from '../../_utils/classnames'

interface IAiOverview {
    loading: boolean
    domain?: DomainDto
    availableViews: AiView[]
    onViewChange: (view: AiView) => void
}

interface IFeatureSummary {
    view: AiView
    description: string
    /** Absent for a service that is always available to whoever can see it. */
    flag?: string
    internalOnly?: boolean
}

const FEATURES: IFeatureSummary[] = [
    {
        view: AiView.BRAND_VOICE,
        flag: FEAT_PUSHLY_AI,
        description:
            'Write your editorial standards once, in your own words, and every title and line of copy the model ' +
            'produces follows them. Rules we can check automatically are enforced on every generation; the rest ' +
            'guides tone, so what goes out sounds like your team rather than a generic assistant.',
    },
    {
        view: AiView.CURATOR,
        flag: FEAT_INTEGRATIONS_VESPA_REC,
        description:
            'Every audience gets the story it is most likely to open, chosen from your own library and scored ' +
            'against what that segment has actually clicked. Set it once on a journey step and each send picks ' +
            'itself, so one campaign lands differently for readers with different interests.',
    },
    {
        view: AiView.PAGE_ANALYSIS,
        description:
            'Reads the pages your audience opens and turns them into something the rest of the suite can use: ' +
            'clean article text, a classification, keywords and imagery. It is what Brand Voice writes from and ' +
            'what the Curator ranks, so the wider the coverage, the better everything downstream gets.',
        internalOnly: true,
    },
]

/**
 * The index for the section. It is deliberately a summary rather than a menu:
 * the services share one pipeline, so the thing worth seeing on arrival is
 * which of them are on and what they are currently doing.
 */
export const AiOverview: FC<IAiOverview> = ({ loading, domain, availableViews, onViewChange }) => {
    const suiteEnabled = domainHasFlag(domain, FEAT_PUSHLY_AI)

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('ai-overview')}>
            <Well
                title={
                    <>
                        <PushlyAi /> Pushly AI
                    </>
                }
                footerMode="hidden"
            >
                <Skeleton loading={loading} active={true} title={false}>
                    <Typography.Paragraph className="ai-muted-text">
                        Pushly AI is a suite of services that read your published content and use it to write and target
                        your notifications. It generates titles, copy and imagery, recommends the article each audience
                        is most likely to click, and holds all of it to a brand voice you define. Each service below has
                        its own settings and its own record of what it did. It never touches content you write yourself.
                    </Typography.Paragraph>
                </Skeleton>
            </Well>

            {/*
             * Without the suite flag there is nothing here to configure, so the
             * section says what Pushly AI is once and points at the person who can
             * turn it on, rather than repeating that on every service in turn.
             */}
            {!loading && !suiteEnabled && (
                <Well hideHeader={true} footerMode="hidden">
                    <EmptyState
                        icon={<LockOutlined />}
                        iconVariant="chip"
                        title="Pushly AI is not enabled for this domain"
                        description="Talk to your account admin to find out what Pushly AI can do for your content."
                    />
                </Well>
            )}

            {FEATURES.filter((feature) => availableViews.includes(feature.view)).map((feature) => {
                const featureEnabled = !feature.flag || domainHasFlag(domain, feature.flag)

                return (
                    <Well
                        key={feature.view}
                        title={
                            <Flex align="center" gap={8}>
                                {AI_VIEW_LABELS[feature.view]}
                                {feature.internalOnly && <Tag className="ai-title-tag">Internal only</Tag>}
                            </Flex>
                        }
                        actions={
                            <Flex align="center" gap={8}>
                                {!featureEnabled && <Tag className="ai-title-tag">Not enabled</Tag>}
                                <Button size="small" onClick={() => onViewChange(feature.view)}>
                                    Open
                                </Button>
                            </Flex>
                        }
                        footerMode="hidden"
                    >
                        <Skeleton loading={loading} active={true} title={false}>
                            <Typography.Paragraph className="ai-muted-text">{feature.description}</Typography.Paragraph>
                        </Skeleton>
                    </Well>
                )
            })}
        </Flex>
    )
}
